// Deliberately the *pure* id module, not `@lib/verbs`: that one carries glyphs, so it drags
// `@pages` and its SCSS in and breaks this file's `npx tsx` unit test. See `./mediaActions`.
import { isMediaVerbId } from '@lib/verbs/ids';
import { MEDIA_ACTION_ORDER, type MediaMenuActionItem } from './mediaActions';

export interface SplitMediaActionsOptions {
  /**
   * The library skips visual indexing. Flips which verb leads: a transcript-only library is a
   * podcast library, so clipping is the thing you came to do.
   */
  isTranscriptOnly: boolean;
  /**
   * Whether the surface is in a state where leading with an accented button is honest at all.
   * `false` hides the CTA entirely rather than dimming it; the verbs stay in the kebab.
   *
   * NOT simply "has the media finished processing", which is what it was called until the
   * caller grew a second condition. `MomentModal` also requires the media's library to have
   * actually answered, because the CTA's identity depends on `isTranscriptOnly` and that reads
   * `false` for a library whose fetch failed as well as for one that genuinely isn't
   * transcript-only. Naming this after one of its inputs made the prop lie about the other.
   *
   * Passed in rather than derived from `actions[].disabled` because neither condition touches
   * the actions' own gates. The enabled-check below still runs, so every route to "no usable
   * CTA" is covered.
   */
  canShowCta: boolean;
}

export interface MediaActionBarModel {
  /** The accented pill, or `null` when there is nothing worth leading with. */
  cta: MediaMenuActionItem | null;
  /** The quiet pill beside it, or `null`. Never set without `cta`. */
  secondary: MediaMenuActionItem | null;
  /** The verb group — above the divider. */
  verbItems: MediaMenuActionItem[];
  /** Housekeeping — below the divider. */
  settingsItems: MediaMenuActionItem[];
}

/**
 * Split the flat action list into the CTA, the pill beside it and the two kebab groups.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURE. No React, no stores, no `process.env`. It is unit-tested; keep it that way.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The rules, in the order they apply:
 *
 * 1. **Prefer** `clip-podcast` in a transcript-only library, the agent verb otherwise. This is
 *    the whole point of the redesign: one obvious button whose identity follows the library.
 * 2. **Fall back** to the other verb when the preferred one is absent or inert. Absent happens
 *    in a build with no `CHAT_URL` (the clipper doesn't exist there at all); inert happens on a
 *    still image, which will never have audio to clip — `IMAGE_CANNOT_CLIP_REASON`. Without this
 *    step a transcript-only library holding one image would render a dead accented button, which
 *    is worse than either alternative.
 * 3. **Suppress** the CTA when `canShowCta` is false — most often because the media is still
 *    processing. The verbs remain in the kebab, dimmed.
 * 4. **Second** clipping beside the agent CTA, as an unaccented pill — only when the agent leads,
 *    so a podcast library keeps its single clip button, and only when clipping is usable, for
 *    the same reason the CTA is: an inert pill beside a live one is a dead control promoted out
 *    of the menu. An image's dimmed clip row stays in the kebab with its reason.
 *
 * Note what step 3 does NOT do: it doesn't drop the verbs. The app's standing rule is that a
 * control you can't use yet stays visible and dimmed rather than vanishing (see the reason
 * builders in `@lib/verbs`); the CTA is the deliberate exception, because an accented pill
 * reading "available once this media finishes processing" is a loud promise on a surface whose
 * whole right-hand panel is already a processing placeholder.
 *
 * Each dimmed row still carries its `disabledReason`, and `BaseMenuItem` renders it as a
 * tooltip — so the desktop menu explains itself. The mobile drawer deliberately doesn't
 * (tooltips don't work on touch); there the dimming alone carries the state, as it does in
 * every other drawer in the app.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO CLIP-SCOPED VERB HERE, AND THAT IS DELIBERATE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The moment modal's search variant hands the Agent a TRIMMED range rather than the whole media.
 * An earlier version modelled that as its own id (`'agent-on-clip'`) substituted in here, which
 * widened the item type across six files and forced three `as MediaMenuAction` casts — including
 * one in `VideoMenuButton`, a file with no clip verb anywhere near it.
 *
 * It bought nothing. Once the clip verb's label was shortened to the catalog's, every field it
 * overrode — label, `disabled`, `disabledReason`, glyph — was byte-identical to what
 * `useMediaVerbs` already produces for `work-with-agent`. The only real difference is the
 * handler, and a handler override belongs at the component that routes the click: see
 * `onWorkWithAgent` on `MediaActionBar`.
 */
export function splitMediaActions(
  actions: MediaMenuActionItem[],
  { isTranscriptOnly, canShowCta }: SplitMediaActionsOptions,
): MediaActionBarModel {
  const preferredId = isTranscriptOnly ? 'clip-podcast' : 'work-with-agent';
  const fallbackId = isTranscriptOnly ? 'work-with-agent' : 'clip-podcast';

  const usable = (id: string) =>
    actions.find((action) => action.id === id && !action.disabled) ?? null;

  const cta = canShowCta ? (usable(preferredId) ?? usable(fallbackId)) : null;
  const secondary = cta?.id === 'work-with-agent' ? usable('clip-podcast') : null;

  // One traversal of the canonical list, then a split — rather than ordering the verbs by
  // mapping a list and the rest by sorting with a comparator, which was two mechanisms for one
  // job ten lines apart. `MEDIA_ACTION_ORDER` opens with `MEDIA_VERB_IDS`, so the verb group
  // cannot drift from the order every other surface's rail uses.
  const ordered = MEDIA_ACTION_ORDER.map((id) => actions.find((action) => action.id === id)).filter(
    (action): action is MediaMenuActionItem =>
      !!action && action.id !== cta?.id && action.id !== secondary?.id,
  );

  return {
    cta,
    secondary,
    verbItems: ordered.filter((action) => isMediaVerbId(action.id)),
    settingsItems: ordered.filter((action) => !isMediaVerbId(action.id)),
  };
}
